/**
 * Main UI controller — wires components together and handles conversion.
 */

import { store } from './state';
import { initEditor, setEditorValue } from './editor';
import { initPreview, renderPreview } from './preview';
import { initControls } from './controls';
import { initColorPalette } from './color-palette';
import { initPullCord } from './pull-cord';
import { initExport } from './export';
import { initSplitter } from './splitter';
import { convert } from '../core/pipeline';

// Module-level buffers for bidirectional editing
let mermaidText = '';
let xmlText = '';

export function initApp(): void {
  initEditor();
  initPreview();
  initControls();
  initColorPalette();
  initPullCord();
  initExport();
  initSplitter();

  // Wire editor mode toggle
  const editorMermaidBtn = document.getElementById('editor-mode-mermaid') as HTMLButtonElement;
  const editorXmlBtn = document.getElementById('editor-mode-xml') as HTMLButtonElement;

  if (editorMermaidBtn && editorXmlBtn) {
    editorMermaidBtn.addEventListener('click', () => {
      if (store.get().editorMode === 'mermaid') return;
      // Save current XML text from editor
      xmlText = (document.getElementById('code-editor') as HTMLTextAreaElement).value;
      // Switch to mermaid
      store.set({ editorMode: 'mermaid' });
      setEditorValue(mermaidText, true);
      editorMermaidBtn.classList.add('active');
      editorXmlBtn.classList.remove('active');
      // Update store with correct input/output for mermaid mode
      store.set({ input: mermaidText, output: xmlText });
      renderPreview();
    });

    editorXmlBtn.addEventListener('click', async () => {
      if (store.get().editorMode === 'xml') return;
      // Save current mermaid text from editor
      mermaidText = (document.getElementById('code-editor') as HTMLTextAreaElement).value;
      // Ensure we have XML output
      if (!xmlText && mermaidText.trim()) {
        await runConversion();
        xmlText = store.get().output;
      }
      // Switch to xml
      store.set({ editorMode: 'xml' });
      setEditorValue(xmlText, true);
      editorXmlBtn.classList.add('active');
      editorMermaidBtn.classList.remove('active');
      // Update store with correct input/output for xml mode
      store.set({ input: xmlText, output: mermaidText });
      renderPreview();
    });
  }

  // Wire preview mode toggle
  const previewMermaidBtn = document.getElementById('preview-mode-mermaid') as HTMLButtonElement;
  const previewXmlBtn = document.getElementById('preview-mode-xml') as HTMLButtonElement;

  if (previewMermaidBtn && previewXmlBtn) {
    previewMermaidBtn.addEventListener('click', async () => {
      if (store.get().previewMode === 'mermaid') return;
      store.set({ previewMode: 'mermaid' });
      previewMermaidBtn.classList.add('active');
      previewXmlBtn.classList.remove('active');
      await renderPreview();
    });

    previewXmlBtn.addEventListener('click', async () => {
      if (store.get().previewMode === 'xml') return;
      store.set({ previewMode: 'xml' });
      previewXmlBtn.classList.add('active');
      previewMermaidBtn.classList.remove('active');
      await renderPreview();
    });
  }

  // Update editor when input changes externally (e.g. diagram type switch)
  let lastInput = store.get().input;
  store.subscribe((state) => {
    if (state.input !== lastInput && state.editorMode === 'mermaid') {
      lastInput = state.input;
      mermaidText = state.input;
      setEditorValue(state.input, true);
    }
  });

  // Listen for conversion trigger
  document.addEventListener('ex-mermaid:convert', async () => {
    await runConversion();
    await renderPreview();
  });

  // Seed editor with default starter diagram
  const defaultInput = store.get().input;
  if (defaultInput.trim()) {
    setEditorValue(defaultInput);
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('ex-mermaid:convert'));
    }, 0);
  }

  // Sync preview mode buttons when store changes (e.g. from controls.ts)
  store.subscribe((state) => {
    if (previewMermaidBtn && previewXmlBtn) {
      if (state.previewMode === 'mermaid') {
        previewMermaidBtn.classList.add('active');
        previewXmlBtn.classList.remove('active');
      } else {
        previewXmlBtn.classList.add('active');
        previewMermaidBtn.classList.remove('active');
      }
    }
  });
}

async function runConversion(): Promise<void> {
  const state = store.get();
  const editorText = (document.getElementById('code-editor') as HTMLTextAreaElement)?.value ?? '';

  if (state.editorMode === 'mermaid') {
    mermaidText = editorText;
    if (!mermaidText.trim()) {
      xmlText = '';
      store.set({ output: '', error: null });
      return;
    }
    try {
      const result = await convert(mermaidText, {
        inputFormat: state.inputFormat,
        outputFormat: state.outputFormat,
        direction: state.direction,
        colors: state.colors,
      });
      xmlText = result;
      store.set({ output: result, error: null, input: mermaidText });
    } catch (err) {
      store.set({ error: (err as Error).message });
      console.error('Conversion error:', err);
    }
  } else {
    // Editor is in XML mode — reverse convert
    xmlText = editorText;
    if (!xmlText.trim()) {
      mermaidText = '';
      store.set({ output: '', error: null });
      return;
    }
    try {
      const result = await convert(xmlText, {
        inputFormat: 'drawio-xml',
        outputFormat: 'mermaid',
        direction: state.direction,
        colors: state.colors,
      });
      mermaidText = result;
      // In xml editor mode: input=xml, output=mermaid (reverse result)
      store.set({ input: xmlText, output: mermaidText, error: null });
    } catch (err) {
      store.set({ error: (err as Error).message });
      console.error('Reverse conversion error:', err);
    }
  }
}
